import {
  Box,
  Button,
  capitalize,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  IGranularity,
  INotification,
  ITableAtribute,
  TSubTypeNotification
} from 'src/models/general';
import ClearIcon from '@mui/icons-material/Clear';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { GET_NOTIFICATION_DETAIL_V2 } from 'src/services/graphql/notification/query';
import useContent from 'src/services/useContent';
import { useAppSelector } from 'src/app/hooks';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { timeAgo } from 'src/utils/timeAgo';
import useCommunity from 'src/services/useCommunity';
import { useLocation } from 'react-router';
import useCampagin from 'src/services/useCampaign';
import useConversations from 'src/services/useConversations';
import useComparison from 'src/services/useComparison';
import useStreams from 'src/services/useStreams';
import useOnlineMedia, {
  IUpdateLabelByFilterParams
} from 'src/services/useOnlineMedia';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  InformationDetailUpdateLabel,
  InformationDetailUpdateSentiment,
  InformationDetailIgnoreStream,
  InformationDetailDownloadMediaOnline,
  InformationDetailDownloadSocialMedia,
  InformationDetailFirstTimeSocialMediaData,
  InformationDetailUpdateContentType
} from 'src/components/notifications';
import { downloadFile } from 'src/utils/downloadByUrl';
import usePeople from 'src/services/usePeople';
import { IAdvanceSearchPeoplePayload } from 'src/models/people';
import { fillPeopleAdvanceSearchParams } from 'src/utils/fillPeopleAdvanceSearchParams';
import InformationDetailUpdateLabelOnm, {
  IUpdateLabelOnmDetail
} from 'src/components/notifications/InformationDetailUpdateLabelOnm';
import InformationDetailUpdateSentimentOnm from 'src/components/notifications/InformationDetailUpdateSentimentOnm';
import InformationDetailIgnoreOnm from 'src/components/notifications/InformationDetailIgnoreOnm';
import moment from 'moment';
import { getStatusColor } from 'src/utils/getStatusColor';
import { Column } from 'docx';
interface ItemActivityProps {
  progresses: Array<INotification>;
  onDelete: (id: string) => void;
  loading?: boolean;
  onClose: () => void;
  onRetry: () => void;
}

interface NotificationSelected {
  id: string;
  subTypeNotification: TSubTypeNotification | undefined;
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: '50vw',
    background: theme.palette.common.white,
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
    fontWeight: 0.1,
    letterSpacing: 0.2,
    lineHeight: 1.4,
    webkitFontSmoothing: 'antialiased',
    textRendering: 'optimizeLegibility'
  }
}));

const StyledBox = styled(Box)(
  ({ theme }) => `
    padding: ${theme.spacing(2)};
    border-radius: ${theme.spacing(1)};
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.19);
    background-color: white;
    border: 2px solid #EEEEEE;
  `
);

const ItemActivity: React.FC<ItemActivityProps> = ({
  progresses,
  onDelete,
  loading = false,
  onClose,
  onRetry
}: ItemActivityProps) => {
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const advanceSearchSocialMedia = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );
  const projectId = useAppSelector((state) => state.users.project.id);
  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );
  const [detail, setDetail] = useState();
  const [notificationSelected, setNotificationSelected] =
    useState<NotificationSelected>({
      id: '',
      subTypeNotification: 'downloadSocialMedia'
    });
  const [isLoadingRetry, setIsLoadingRetry] = useState<boolean>(false);
  const [openError, setOpenError] = useState<boolean>(false);

  const fillAdvanceSearchSocialMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchSocialMedia);
  }, [advanceSearchSocialMedia]);

  const granularity: IGranularity = {
    unit: 'day',
    value: 1
  };

  const initialTableAttribute: ITableAtribute = {
    columnName: 'dateColumn',
    limit: 20,
    page: 1,
    sortingMethod: 'desc'
  };

  const { pathname } = useLocation();
  const pathValue = pathname.split('/')[2];

  const { getContentAllMetric } = useContent();
  const { getCommunityAllMetric } = useCommunity();
  const { getAllMetricCampaign } = useCampagin();
  const { getConversationAllMetric } = useConversations();
  const { getAllMetricCompatisonSosmed } = useComparison();
  const {
    getArticle,
    updateLabelByFilter,
    updateSentimentByFilter,
    removeArticlesByFilter
  } = useOnlineMedia();

  const {
    getStreamOutputWithPagination,
    getIgnoredStreamOutputWithPagination
  } = useStreams();

  const { getPeople, getIgnoredPeopleList } = usePeople();

  const objectIdList = useAppSelector(
    (state) => state.analyticArguments.objectIdList
  );

  const timezone = useAppSelector((state) => state.users.project.timezone);

  const finalPayload = {
    ...fillAdvanceSearchSocialMedia,
    projectId,
    ...dateRangeFormatted,
    objectList: objectIdList,
    granularity,
    timezone,
    ...initialTableAttribute
  };

  const advanceSearchOnlineMedia = useAppSelector(
    (state) => state.storeAdvanceSearchOnlineMedia
  );
  const fillAdvanceSearchOnlineMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchOnlineMedia);
  }, [advanceSearchOnlineMedia]);
  const { articleList, languageSelected } = useAppSelector(
    (state) => state.storeOnlineMediaStream
  );

  const clippingId = pathname.split('/')[3];

  const onmFinalPayload = {
    ...fillAdvanceSearchOnlineMedia,
    languageList: languageSelected === 'all-language' ? [] : [languageSelected],
    projectId,
    ...dateRangeFormatted,
    granularity,
    timezone,
    clippingId: clippingId,
    page: articleList.data.page,
    limit: articleList.data.limit
  };

  const { people } = useAppSelector((state) => state.storePeople);

  const stateTablePeople = {
    sizeToFetch: people.data.limit,
    page: people.data.page
  };

  const peopleAdvanceSearch = useAppSelector(
    (state) => state.storePeopleAdvanceSearch
  );

  const peopleAdvanceSearchParams = useMemo(() => {
    return fillPeopleAdvanceSearchParams(peopleAdvanceSearch);
  }, [peopleAdvanceSearch]);

  const peoplePayload: IAdvanceSearchPeoplePayload = {
    projectId: projectId,
    granularity: granularity,
    objectList: objectIdList,
    timezone,
    ...peopleAdvanceSearchParams,
    locationValidationList: [],
    ...stateTablePeople,
    ...dateRangeFormatted
  };

  const [getDetailNotification, responseDetailNotification] = useLazyQuery(
    GET_NOTIFICATION_DETAIL_V2
  );

  const handleRefresh = () => {
    onClose();
    switch (pathValue) {
      case 'content':
        getContentAllMetric(finalPayload);
        return;
      case 'community':
        getCommunityAllMetric(finalPayload);
        return;
      case 'campaign':
        getAllMetricCampaign(finalPayload);
        return;
      case 'conversation':
        getConversationAllMetric(finalPayload);
        return;
      case 'comparison':
        getAllMetricCompatisonSosmed(finalPayload);
        return;
      case 'social_media':
        getStreamOutputWithPagination(finalPayload);
        getIgnoredStreamOutputWithPagination(finalPayload);
        return;
      case 'clipping':
        getArticle(onmFinalPayload);
        return;
      case 'people_panel':
        getPeople(peoplePayload);
        getIgnoredPeopleList(peoplePayload);
        return;
    }
  };

  const client = useApolloClient();
  const handleClickDownload = function (subTypeNotification, id) {
    client
      .query({
        query: GET_NOTIFICATION_DETAIL_V2,
        variables: {
          notificationId: id
        }
      })
      .then((response) => {
        downloadFile(
          response.data.notification_getNotificationDetail[
            subTypeNotification === 'downloadCustomReport'
              ? 'downloadReport'
              : subTypeNotification
          ].link
        );
      });
  };

  const handleRetryUpdateLabelOnm = async (id: string) => {
    try {
      setIsLoadingRetry(true);
      const res = await getDetailNotification({
        variables: { notificationId: id }
      });
      const prevVariables: IUpdateLabelOnmDetail =
        res.data.notification_getNotificationDetail.updateLabelOnm;
      const formattedDateRange = {
        start: moment(prevVariables.start)
          .utcOffset(7)
          .format('YYYY-MM-DD HH:mm:ss'),
        end: moment(prevVariables.end)
          .utcOffset(7)
          .format('YYYY-MM-DD HH:mm:ss')
      };

      const retryVariables: IUpdateLabelByFilterParams = {
        ...prevVariables,
        ...formattedDateRange,
        granularity: { unit: 'day', value: 1 } as IGranularity,
        timezone,
        clippingId: prevVariables.clipping.id
      };
      await updateLabelByFilter({ variables: retryVariables });
      onRetry();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingRetry(false);
    }
  };

  const handleRetryUpdateSentimentOnm = async (id: string) => {
    try {
      setIsLoadingRetry(true);
      const res = await getDetailNotification({
        variables: { notificationId: id }
      });
      const prevVariables =
        res.data.notification_getNotificationDetail.updateSentimentOnm;
      const formattedDateRange = {
        start: moment(prevVariables.start)
          .utcOffset(7)
          .format('YYYY-MM-DD HH:mm:ss'),
        end: moment(prevVariables.end)
          .utcOffset(7)
          .format('YYYY-MM-DD HH:mm:ss')
      };

      const retryVariables = {
        ...prevVariables,
        ...formattedDateRange,
        granularity: { unit: 'day', value: 1 } as IGranularity,
        timezone,
        clippingId: prevVariables.clipping.id
      };
      await updateSentimentByFilter({ variables: retryVariables });
      onRetry();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingRetry(false);
    }
  };

  const handleRetryUpdateVisibilityOnm = async (id: string) => {
    try {
      setIsLoadingRetry(true);
      const res = await getDetailNotification({
        variables: { notificationId: id }
      });
      const prevVariables =
        res.data.notification_getNotificationDetail.updateVisibilityOnm;
      const formattedDateRange = {
        start: moment(prevVariables.start)
          .utcOffset(7)
          .format('YYYY-MM-DD HH:mm:ss'),
        end: moment(prevVariables.end)
          .utcOffset(7)
          .format('YYYY-MM-DD HH:mm:ss')
      };

      const retryVariables = {
        ...prevVariables,
        ...formattedDateRange,
        granularity: { unit: 'day', value: 1 } as IGranularity,
        timezone,
        clippingId: prevVariables.clipping.id
      };
      await removeArticlesByFilter({ variables: retryVariables });
      onRetry();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingRetry(false);
    }
  };

  const BUTTON_CONFIG: Record<
    TSubTypeNotification,
    { type: 'refresh' | 'download' | 'retry'; label: string }
  > = {
    updateLabel: { type: 'refresh', label: 'Refresh' },
    updateSentiment: { type: 'refresh', label: 'Refresh' },
    ignoreStream: { type: 'refresh', label: 'Refresh' },
    downloadSocialMedia: { type: 'download', label: 'Download' },
    downloadOnlineMedia: { type: 'download', label: 'Download' },
    downloadOfflineMedia: { type: 'download', label: 'Download' },
    downloadCustomDashboard: { type: 'download', label: 'Download' },
    firstTimeSocialMediaData: { type: 'refresh', label: 'Refresh' },
    onDemandOnlineMediaSearch: { type: 'refresh', label: 'Refresh' },
    downloadCustomReport: { type: 'download', label: 'Download' },
    downloadOnlineMediaClustering: { type: 'download', label: 'Download' },
    downloadSummaryOnlineMedia: { type: 'download', label: 'Download' },
    downloadSummary: { type: 'download', label: 'Download' },
    updateAgeRangeEngagedUser: { type: 'refresh', label: 'Refresh' },
    updateGenderEngagedUser: { type: 'refresh', label: 'Refresh' },
    updateInterestEngagedUser: { type: 'refresh', label: 'Refresh' },
    updateLabelEngagedUser: { type: 'refresh', label: 'Refresh' },
    updateLocationEngagedUser: { type: 'refresh', label: 'Refresh' },
    ignoreEngagedUser: { type: 'refresh', label: 'Refresh' },
    updateContentMediaType: { type: 'refresh', label: 'Refresh' },
    reClipping: { type: 'refresh', label: 'Refresh' },
    updateLabelOnm: { type: 'retry', label: 'Retry' },
    updateSentimentOnm: { type: 'retry', label: 'Retry' },
    updateVisibilityOnm: { type: 'retry', label: 'Retry' },
    copyKeyword: { type: 'retry', label: 'Retry' },
    downloadKemenluReport: { type: 'download', label: 'Download' }
  };

  const renderSuccessButton = (
    buttonType: 'refresh' | 'download' | 'retry',
    label: string,
    subTypeNotification: TSubTypeNotification,
    currentProgress: number,
    totalProgress: number,
    id: string
  ) => {
    const commonButtonProps = {
      sx: { borderRadius: theme.spacing(1) },
      variant: 'contained' as 'text' | 'outlined' | 'contained',
      size: 'small' as 'small' | 'medium' | 'large'
    };

    const getRetryHandler = (type: TSubTypeNotification) => {
      const handlers = {
        updateLabelOnm: handleRetryUpdateLabelOnm,
        updateSentimentOnm: handleRetryUpdateSentimentOnm,
        updateVisibilityOnm: handleRetryUpdateVisibilityOnm
      };
      return handlers[type];
    };

    if (buttonType === 'download') {
      return (
        <Button
          {...commonButtonProps}
          onClick={() => handleClickDownload(subTypeNotification, id)}
        >
          {label}
        </Button>
      );
    }

    if (buttonType === 'retry' && currentProgress < totalProgress) {
      const retryHandler = getRetryHandler(subTypeNotification);
      return (
        <Button
          {...commonButtonProps}
          onClick={() => retryHandler(id)}
          disabled={isLoadingRetry}
        >
          {isLoadingRetry ? 'Retrying..' : label}
        </Button>
      );
    }

    if (
      subTypeNotification === 'firstTimeSocialMediaData' ||
      subTypeNotification === 'copyKeyword'
    )
      return <div />;

    // Default to refresh button for all other cases
    return (
      <Button {...commonButtonProps} onClick={() => handleRefresh()}>
        Refresh
      </Button>
    );
  };

  const setSuccess = (
    subTypeNotification: TSubTypeNotification,
    currentProgress: number,
    totalProgress: number,
    id: string
  ) => {
    const config = BUTTON_CONFIG[subTypeNotification];
    if (!config) return <div />;

    return renderSuccessButton(
      config.type,
      config.label,
      subTypeNotification,
      currentProgress,
      totalProgress,
      id
    );
  };

  const handleMouseOut = () => {
    setNotificationSelected({
      id: '',
      subTypeNotification: undefined
    });
    setDetail(undefined);
  };

  const handleGetDetailNotification = (
    id: string,
    subTypeNotification: TSubTypeNotification
  ) => {
    setNotificationSelected({
      id,
      subTypeNotification
    });
    getDetailNotification({
      variables: {
        notificationId: id
      }
    });
  };

  useEffect(() => {
    if (!responseDetailNotification) return;
    setDetail(
      responseDetailNotification.data?.notification_getNotificationDetail
    );
  }, [responseDetailNotification]);

  const renderProcess = (
    subTypeNotification: TSubTypeNotification | undefined,
    detail
  ) => {
    if (!subTypeNotification) return 'Loading...';
    if (subTypeNotification !== 'copyKeyword' && !detail[subTypeNotification])
      return 'Loading...';
    if (
      // for 'copyKeyword' subType, the detail is in `firstTimeSocialMediaData`
      subTypeNotification === 'copyKeyword' &&
      !detail['firstTimeSocialMediaData']
    )
      return 'Loading..';
    switch (subTypeNotification) {
      case 'updateLabel':
        return (
          <InformationDetailUpdateLabel detail={detail[subTypeNotification]} />
        );
      case 'updateSentiment':
        return (
          <InformationDetailUpdateSentiment
            detail={detail[subTypeNotification]}
          />
        );
      case 'ignoreStream':
        return (
          <InformationDetailIgnoreStream detail={detail[subTypeNotification]} />
        );
      case 'downloadSocialMedia':
        return (
          <InformationDetailDownloadSocialMedia
            detail={detail[subTypeNotification]}
          />
        );
      case 'downloadOnlineMedia':
        return (
          <InformationDetailDownloadMediaOnline
            detail={detail[subTypeNotification]}
          />
        );
      case 'downloadOfflineMedia':
        return (
          <InformationDetailDownloadMediaOnline
            detail={detail[subTypeNotification]}
          />
        );
      // case 'downloadCustomReport':
      //   return (
      //     <InformationDetailDownloadCostumReport
      //       detail={detail[subTypeNotification]}
      //     />
      //   );
      case 'firstTimeSocialMediaData':
        return (
          <InformationDetailFirstTimeSocialMediaData
            detail={detail[subTypeNotification]}
          />
        );
      case 'copyKeyword':
        return (
          <InformationDetailFirstTimeSocialMediaData
            detail={detail['firstTimeSocialMediaData']}
          />
        );
      case 'onDemandOnlineMediaSearch':
        return (
          <InformationDetailUpdateSentiment
            detail={detail[subTypeNotification]}
          />
        );
      case 'updateContentMediaType':
        return (
          <InformationDetailUpdateContentType
            detail={detail[subTypeNotification]}
          />
        );
      case 'updateAgeRangeEngagedUser':
        return (
          <div>{JSON.stringify(detail[subTypeNotification], undefined, 2)}</div>
        );
      case 'updateGenderEngagedUser':
        return (
          <div>{JSON.stringify(detail[subTypeNotification], undefined, 2)}</div>
        );
      case 'updateInterestEngagedUser':
        return (
          <div>{JSON.stringify(detail[subTypeNotification], undefined, 2)}</div>
        );
      case 'updateLocationEngagedUser':
        return (
          <div>{JSON.stringify(detail[subTypeNotification], undefined, 2)}</div>
        );
      case 'updateLabelOnm':
        return (
          <InformationDetailUpdateLabelOnm
            detail={detail[subTypeNotification]}
          />
        );
      case 'updateSentimentOnm':
        return (
          <InformationDetailUpdateSentimentOnm
            detail={detail[subTypeNotification]}
          />
        );
      case 'updateVisibilityOnm':
        return (
          <InformationDetailIgnoreOnm detail={detail[subTypeNotification]} />
        );

      default:
        return <div>{subTypeNotification}</div>;
    }
  };

  const getLinearProgressValue = (
    currentProgress: number,
    totalProgress: number,
    status: string
  ): number => {
    // if total progress and current progress value is 0
    if (isNaN((currentProgress * 100) / totalProgress)) {
      return status === 'success' ? 100 : 0;
    }
    return (currentProgress * 100) / totalProgress;
  };

  return (
    <>
      {progresses.map(
        ({
          id,
          title,
          typeNotification,
          subTypeNotification,
          status,
          currentProgress,
          totalProgress,
          createdAt
        }) => {
          return (
            <StyledBox sx={{ pt: theme.spacing(2) }} key={id}>
              <Stack
                direction={matchesUpMd ? "row" : 'column' }
                justifyContent="space-between"
                alignItems="center"
                spacing={matchesUpMd ? 0 : 1 }
                marginBottom={matchesUpMd ? 0 : 1}
              >
                <Stack direction="row" alignItems="center" spacing={1} justifyContent={matchesUpMd ? 'flex-start' : 'unset'} width={matchesUpMd ? 'unset' : '100%'}>
                  <Typography variant="h6">{title}</Typography>
                  <Chip
                    size="small"
                    label={capitalize(status)}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: `${getStatusColor(status)}30`,
                      color: getStatusColor(status)
                    }}
                  />
                </Stack>
                <Stack direction="row" alignItems={"center"} width={matchesUpMd ? 'unset' : '100%'} justifyContent={ matchesUpMd ? 'unset' : 'flex-end' } spacing={1}>
                  <CustomTooltip
                    title={
                      responseDetailNotification.loading
                        ? 'Loading...'
                        : detail
                        ? renderProcess(
                            notificationSelected?.subTypeNotification,
                            detail
                          )
                        : ''
                    }
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    open={notificationSelected.id === id && !openError}
                    arrow
                    onMouseOver={() =>
                      handleGetDetailNotification(id, subTypeNotification)
                    }
                    onMouseLeave={handleMouseOut}
                  >
                    <Button
                      variant="outlined"
                      sx={{ borderRadius: theme.spacing(1) }}
                      size="small"
                    >
                      Information
                    </Button>
                  </CustomTooltip>
                  {status === 'success'
                    ? setSuccess(
                        subTypeNotification,
                        currentProgress,
                        totalProgress,
                        id
                      )
                    : null}
                  <IconButton
                    size="small"
                    onClick={() => onDelete(id)}
                    disabled={loading}
                  >
                    <ClearIcon
                      sx={{
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5),
                        color: '#9D9D9C'
                      }}
                    />
                  </IconButton>
                </Stack>
              </Stack>
              <Typography variant="caption">
                Started {timeAgo(createdAt)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getLinearProgressValue(
                  currentProgress,
                  totalProgress,
                  status
                )}
                sx={{ my: theme.spacing(0.5) }}
                color={
                  status === 'error'
                    ? 'error'
                    : status === 'success'
                    ? 'success'
                    : 'primary'
                }
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                {status === 'pending' || status === 'running' ? (
                  <Typography variant="caption">
                    {status === 'pending' ? 'Pending' : 'Running'}
                    <span
                      style={{
                        fontWeight: 700,
                        color: theme.palette.common.black
                      }}
                    >
                      {` ${getLinearProgressValue(
                        currentProgress,
                        totalProgress,
                        status
                      ).toFixed()}%`}
                    </span>
                  </Typography>
                ) : status === 'error' ? (
                  <Stack spacing={1}>
                    <Typography
                      variant="caption"
                      sx={{ color: `${theme.colors.error.main}` }}
                    >
                      Failed
                    </Typography>
                    {detail &&
                    (detail as any)[notificationSelected?.subTypeNotification]
                      ?.error ? (
                      <CustomTooltip
                        title={
                          <Typography variant="h6" color="black">
                            {responseDetailNotification.loading
                              ? 'Loading...'
                              : detail
                              ? (detail as any)[
                                  notificationSelected?.subTypeNotification
                                ]?.error ?? ''
                              : ''}
                          </Typography>
                        }
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        open={notificationSelected.id === id && openError}
                        arrow
                        onMouseOver={() => {
                          handleGetDetailNotification(id, subTypeNotification);
                          setOpenError(true);
                        }}
                        onMouseLeave={() => {
                          handleMouseOut();
                          setOpenError(false);
                        }}
                      >
                        <Button
                          variant="text"
                          sx={{ borderRadius: theme.spacing(1), px: 0 }}
                          size="small"
                          color="error"
                          startIcon={<InfoOutlinedIcon />}
                        >
                          Error why?
                        </Button>
                      </CustomTooltip>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </StyledBox>
          );
        }
      )}
    </>
  );
};

export default ItemActivity;
